import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Lock, Wand2, Send, MessageSquare, X, Layers, Loader2, Compass, Sparkles, Images, Gamepad2 } from "lucide-react";
import ParticleField from "@/components/hud/ParticleField";

export default function Simulation() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [environments, setEnvironments] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [activeEnv, setActiveEnv] = useState(null);
  const [activePersona, setActivePersona] = useState(null);
  const [state, setState] = useState(null);

  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [bgImage, setBgImage] = useState(null);
  const [renders, setRenders] = useState([]);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  const loadState = useCallback(async () => {
    const s = await api.get("/sim/state").then((r) => r.data).catch(() => null);
    setState(s);
  }, []);

  useEffect(() => {
    api.get("/content/environments").then((r) => {
      setEnvironments(r.data);
      const first = r.data.find((e) => !e.locked);
      setActiveEnv(first || r.data[0]);
      if (first) setBgImage(first.image);
    });
    api.get("/content/personas").then((r) => {
      setPersonas(r.data);
      const firstP = r.data.find((p) => !p.locked);
      setActivePersona(firstP || r.data[0]);
    });
    loadState();
    api.get("/sim/history").then((r) => setRenders(r.data)).catch(() => {});
  }, [loadState]);

  const onParallax = (e) => {
    const x = (e.clientX / window.innerWidth - 0.5) * 22;
    const y = (e.clientY / window.innerHeight - 0.5) * 22;
    setParallax({ x, y });
  };

  const envRenders = renders.filter((r) => r.environment_id === activeEnv?.id);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatOpen]);

  const selectEnv = (env) => {
    if (env.locked) {
      toast.error(`${env.name} requires higher clearance`);
      return;
    }
    setActiveEnv(env);
    setBgImage(env.image);
    setMessages([]);
    setSessionId(null);
  };

  const selectPersona = (p) => {
    if (p.locked) {
      toast.error(`${p.name} is a premium persona`);
      return;
    }
    setActivePersona(p);
    setMessages([]);
    setSessionId(null);
  };

  const handleQuotaError = (e) => {
    const detail = e?.response?.data?.detail;
    if (detail === "QUOTA_EXCEEDED") {
      toast.error("Generation quota depleted. Upgrade to continue.");
      navigate("/pricing");
      return true;
    }
    if (detail === "ENVIRONMENT_LOCKED" || detail === "PERSONA_LOCKED") {
      toast.error("Insufficient clearance.");
      return true;
    }
    return false;
  };

  const generate = async () => {
    if (!prompt.trim() || !activeEnv) return;
    setGenerating(true);
    try {
      const res = await api.post("/sim/generate", {
        environment_id: activeEnv.id,
        prompt: prompt.trim(),
      });
      setBgImage(res.data.image);
      toast.success("World rendered");
      setRenders((prev) => [{
        gen_id: res.data.gen_id, image: res.data.image, prompt: res.data.prompt,
        environment_id: activeEnv.id,
      }, ...prev]);
      setPrompt("");
      loadState();
      refresh();
    } catch (e) {
      if (!handleQuotaError(e)) toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !activePersona || !activeEnv) return;
    const text = chatInput.trim();
    setChatInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setChatBusy(true);
    try {
      const res = await api.post("/sim/chat", {
        session_id: sessionId,
        environment_id: activeEnv.id,
        persona_id: activePersona.id,
        message: text,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
      loadState();
      refresh();
    } catch (e) {
      if (!handleQuotaError(e)) {
        toast.error(e?.response?.data?.detail || "Transmission failed");
        setMessages((m) => m.slice(0, -1));
      }
    } finally {
      setChatBusy(false);
    }
  };

  const limit = state?.gen_limit ?? 0;
  const used = state?.gen_used ?? 0;
  const uncapped = limit === -1;

  return (
    <div className="relative min-h-screen bg-app overflow-hidden" data-testid="simulation-page" onMouseMove={onParallax}>
      <Seo title="Simulation" description="Explore immersive AI-generated sci-fi simulation worlds and converse with VFX-grade personas." path="/simulation" noindex />
      {/* Immersive background */}
      <div className="fixed inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={bgImage}
            src={bgImage}
            alt="environment"
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 0.55, scale: 1.06 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="w-full h-full object-cover"
            style={{ transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(1.06)`, transition: "transform 0.3s ease-out" }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-app via-app/60 to-app/30" />
        <div className="absolute inset-0 hud-grid opacity-20" />
        <div className="absolute inset-0 opacity-70" style={{ color: activeEnv?.accent || "#00E5FF" }}>
          <ParticleField color={hexToRgb(activeEnv?.accent) || "0,229,255"} />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 pt-8 pb-32">
        {/* top telemetry */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="glass px-4 py-2 brackets" data-testid="sim-env-readout">
            <div className="font-mono text-[10px] tracking-widest text-slate-400">CURRENT SECTOR</div>
            <div className="font-head text-lg uppercase" style={{ color: activeEnv?.accent || "#00E5FF" }}>
              {activeEnv?.name || "—"}
            </div>
          </div>
          <div className="glass px-4 py-2" data-testid="sim-quota-readout">
            <div className="font-mono text-[10px] tracking-widest text-slate-400">GENERATIONS</div>
            <div className="font-head text-lg text-cyan text-glow-cyan">
              {uncapped ? "∞ UNCAPPED" : `${used} / ${limit}`}
            </div>
          </div>
        </div>

        {/* Environment selector */}
        <div className="flex items-center gap-2 mb-3 font-mono text-[10px] tracking-widest text-cyan/70">
          <Layers className="w-3.5 h-3.5" /> SECTOR SELECT
        </div>
        <div className="flex gap-3 overflow-x-auto pb-3 mb-8" data-testid="env-selector">
          {environments.map((env) => (
            <button
              key={env.id}
              onClick={() => selectEnv(env)}
              data-testid={`env-option-${env.id}`}
              className={`relative flex-shrink-0 w-36 h-24 overflow-hidden border transition-all ${
                activeEnv?.id === env.id ? "border-cyan box-glow-cyan" : "border-white/10 hover:border-cyan/50"
              } ${env.locked ? "opacity-60" : ""}`}
            >
              <img src={env.image} alt={env.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-app/90 to-transparent" />
              {env.locked && (
                <div className="absolute inset-0 flex items-center justify-center bg-app/50">
                  <Lock className="w-4 h-4 text-amber" />
                </div>
              )}
              {env.owned && !env.locked && (
                <div className="absolute top-1 right-1 font-mono text-[7px] tracking-widest px-1.5 py-0.5 border border-cyan/50 text-cyan bg-app/70" data-testid={`env-owned-${env.id}`}>
                  OWNED
                </div>
              )}
              <span className="absolute bottom-1 left-2 font-mono text-[9px] uppercase tracking-wider text-slate-200">
                {env.name}
              </span>
            </button>
          ))}
        </div>

        {/* Codex — lore + objective make the world explorable */}
        {activeEnv && (
          <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="codex-panel">
            <div className="glass clip-chamfer p-4 brackets">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70 mb-2">
                <Compass className="w-3.5 h-3.5" /> OBJECTIVE
              </div>
              <p className="text-sm text-slate-200 leading-snug">{activeEnv.objective}</p>
              {!activeEnv.locked ? (
                <button
                  onClick={() => navigate(`/simulation/world/${activeEnv.id}`)}
                  data-testid="enter-world-btn"
                  className="btn-hud mt-4 w-full flex items-center justify-center gap-2 border border-cyan text-cyan py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
                >
                  <Gamepad2 className="w-4 h-4" /> Enter Interactive World
                </button>
              ) : (
                <button
                  onClick={() => navigate("/pricing")}
                  data-testid="enter-world-locked"
                  className="btn-hud mt-4 w-full flex items-center justify-center gap-2 border border-amber/40 text-amber py-2.5 text-sm hover:bg-amber hover:text-app"
                >
                  <Lock className="w-4 h-4" /> Unlock Sector
                </button>
              )}
            </div>
            <div className="glass clip-chamfer p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-amber/80 mb-2">
                <Sparkles className="w-3.5 h-3.5" /> CODEX // {activeEnv.biome?.toUpperCase()}
              </div>
              <p className="text-sm text-slate-400 italic leading-snug">{activeEnv.lore}</p>
            </div>
          </div>
        )}

        {/* Generation console */}
        <div className="glass-strong clip-chamfer brackets p-6 max-w-3xl" data-testid="generation-console">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70 mb-4">
            <Wand2 className="w-3.5 h-3.5" /> SCENE SYNTHESIZER
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={`Describe what manifests in ${activeEnv?.name || "the sector"}...`}
            data-testid="generation-prompt-input"
            rows={3}
            className="w-full bg-cyan/5 border border-cyan/20 focus:border-cyan text-slate-100 px-4 py-3 font-body outline-none resize-none transition-colors"
          />
          {/* Prompt spark chips — gamified exploration */}
          {activeEnv?.prompt_chips?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="prompt-chips">
              {activeEnv.prompt_chips.map((chip, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(chip)}
                  data-testid={`prompt-chip-${i}`}
                  className="font-mono text-[10px] tracking-wide px-3 py-1.5 border border-cyan/20 text-cyan/80 hover:border-cyan hover:bg-cyan/10 transition-colors"
                >
                  + {chip}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-4 gap-3">
            <span className="font-mono text-[10px] text-slate-500">
              ENGINE: {state?.tier_name?.toUpperCase() || "INITIATE"} {state?.vfx ? "· VFX" : ""}
            </span>
            <button
              onClick={generate}
              disabled={generating || !prompt.trim()}
              data-testid="generate-btn"
              className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-6 py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app disabled:opacity-50"
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> RENDERING</> : <><Wand2 className="w-4 h-4" /> Generate</>}
            </button>
          </div>
        </div>

        {/* Render filmstrip — your renders for this sector; click to re-inhabit */}
        {envRenders.length > 0 && (
          <div className="mt-8 max-w-5xl" data-testid="render-filmstrip">
            <div className="flex items-center gap-2 mb-3 font-mono text-[10px] tracking-widest text-cyan/70">
              <Images className="w-3.5 h-3.5" /> SECTOR ARCHIVE · {envRenders.length}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {envRenders.slice(0, 12).map((r) => (
                <button
                  key={r.gen_id}
                  onClick={() => setBgImage(r.image)}
                  data-testid={`filmstrip-${r.gen_id}`}
                  className={`group relative flex-shrink-0 w-28 h-20 overflow-hidden border transition-all ${
                    bgImage === r.image ? "border-cyan box-glow-cyan" : "border-white/10 hover:border-cyan/50"
                  }`}
                  title={r.prompt}
                >
                  <img src={r.image} alt={r.prompt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-app/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={() => setChatOpen(true)}
        data-testid="open-chat-btn"
        className="fixed bottom-6 right-6 z-30 btn-hud flex items-center gap-2 glass-strong border border-amber/50 text-amber px-5 py-3 box-glow-amber hover:bg-amber hover:text-app"
      >
        <MessageSquare className="w-4 h-4" /> {activePersona?.name || "PERSONA"}
      </button>

      {/* Chat drawer */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] glass-strong border-l border-cyan/20 flex flex-col"
            data-testid="chat-drawer"
          >
            <div className="p-4 pr-6 border-b border-cyan/15 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-widest text-slate-500">COMMS LINK</div>
                <div className="font-head text-xl uppercase" style={{ color: activePersona?.accent }}>
                  {activePersona?.name} <span className="text-xs text-slate-500">· {activePersona?.role}</span>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} data-testid="close-chat-btn" className="shrink-0 text-slate-400 hover:text-cyan p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* persona selector */}
            <div className="flex gap-2 p-3 overflow-x-auto border-b border-cyan/10">
              {personas.map((p) => (
                <button
                  key={p.id}
                  onClick={() => selectPersona(p)}
                  data-testid={`persona-option-${p.id}`}
                  className={`flex-shrink-0 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider border transition-all ${
                    activePersona?.id === p.id ? "border-cyan text-cyan" : "border-white/10 text-slate-400"
                  } ${p.locked ? "opacity-50" : ""}`}
                >
                  {p.locked && <Lock className="inline w-3 h-3 mr-1 text-amber" />}{p.name}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-slate-400 italic text-sm glass p-4 brackets">
                  {activePersona?.greeting}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm ${
                    m.role === "user"
                      ? "bg-cyan/15 border border-cyan/30 text-slate-100"
                      : "glass border-l-2 text-slate-200"
                  }`} style={m.role !== "user" ? { borderLeftColor: activePersona?.accent } : {}}>
                    {m.content}
                  </div>
                </div>
              ))}
              {chatBusy && (
                <div className="flex justify-start">
                  <div className="glass px-4 py-2.5 text-sm text-cyan/70 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> transmitting...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="p-4 border-t border-cyan/15 flex items-center gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendChat()}
                placeholder="Transmit message..."
                data-testid="chat-input"
                className="flex-1 bg-cyan/5 border border-cyan/20 focus:border-cyan text-slate-100 px-3 py-2.5 font-body text-sm outline-none"
              />
              <button
                onClick={sendChat}
                disabled={chatBusy || !chatInput.trim()}
                data-testid="send-chat-btn"
                className="btn-hud border border-cyan text-cyan p-2.5 hover:bg-cyan hover:text-app disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace("#", "");
  const bigint = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  if (Number.isNaN(bigint)) return null;
  return `${(bigint >> 16) & 255},${(bigint >> 8) & 255},${bigint & 255}`;
}

